// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useRef } from 'react'

interface PinInputProps {
  value: string[]
  onChange: (value: string[]) => void
  length?: number
}

export function PinInput({ value, onChange, length = 4 }: PinInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(length).fill(null))

  function handleChange(index: number, raw: string) {
    if (!/^\d?$/.test(raw)) return
    const next = [...value]
    next[index] = raw
    onChange(next)
    if (raw && index < length - 1) inputRefs.current[index + 1]?.focus()
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  return (
    <div className="flex gap-3">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={el => { inputRefs.current[i] = el }}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={value[i] ?? ''}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          className={[
            'w-14 h-14 rounded-xl border text-center text-xl font-bold bg-secondary dark:bg-input-bg text-foreground',
            'outline-none focus:ring-2 transition-colors',
            value[i]
              ? 'border-primary focus:ring-primary/30'
              : 'border-border focus:border-primary focus:ring-primary/20',
          ].join(' ')}
        />
      ))}
    </div>
  )
}
